import React, { useState, useMemo } from 'react';
import { getPsychologyLogs, savePsychologyLog, getTrades } from '../../services/storage';

const THEME = {
  colors: {
    bg: '#0a0e1a', card: '#111827', border: '#1e293b',
    text: '#e2e8f0', textMuted: '#94a3b8',
    green: '#00ff88', red: '#ff3333', gold: '#ffd700',
    blue: '#00d4ff', purple: '#a78bfa', orange: '#fb923c',
  },
};

const S = {
  page:    { padding: '16px', color: THEME.colors.text, fontFamily: 'monospace', maxWidth: '100%' },
  title:   { fontSize: '18px', fontWeight: '700', color: THEME.colors.gold, marginBottom: '4px', letterSpacing: '2px' },
  sub:     { fontSize: '11px', color: THEME.colors.textMuted, marginBottom: '16px', letterSpacing: '1px' },
  card:    (bg) => ({ background: bg || THEME.colors.card, border: `1px solid ${THEME.colors.border}`, borderRadius: '10px', padding: '14px', marginBottom: '12px' }),
  section: { fontSize: '10px', fontWeight: '700', color: THEME.colors.textMuted, letterSpacing: '2px', marginBottom: '10px' },
  label:   { fontSize: '11px', color: THEME.colors.textMuted, marginBottom: '4px' },
  input:   { width: '100%', background: '#0d1117', border: `1px solid ${THEME.colors.border}`, borderRadius: '6px', padding: '8px 10px', color: THEME.colors.text, fontSize: '13px', fontFamily: 'monospace', boxSizing: 'border-box', resize: 'vertical' },
  btn:     (c) => ({ background: c === 'green' ? 'rgba(0,255,136,0.15)' : c === 'primary' ? 'rgba(0,212,255,0.15)' : c === 'gold' ? 'rgba(255,215,0,0.15)' : 'rgba(255,255,255,0.05)', border: `1px solid ${c === 'green' ? THEME.colors.green : c === 'primary' ? THEME.colors.blue : c === 'gold' ? THEME.colors.gold : THEME.colors.border}`, borderRadius: '6px', padding: '8px 14px', color: c === 'green' ? THEME.colors.green : c === 'primary' ? THEME.colors.blue : c === 'gold' ? THEME.colors.gold : THEME.colors.text, fontSize: '12px', cursor: 'pointer', fontFamily: 'monospace', fontWeight: '600' }),
  tabBtn:  (a) => ({ background: a ? 'rgba(0,212,255,0.15)' : 'rgba(255,255,255,0.04)', border: `1px solid ${a ? THEME.colors.blue : THEME.colors.border}`, borderRadius: '6px', padding: '7px 6px', color: a ? THEME.colors.blue : THEME.colors.textMuted, fontSize: '11px', cursor: 'pointer', fontFamily: 'monospace', flex: 1 }),
};

const EMOTIONS = [
  { id: 'calm',           label: '😌 Calm',           good: true  },
  { id: 'focused',        label: '🎯 Focused',         good: true  },
  { id: 'confident',      label: '💪 Confident',       good: true  },
  { id: 'patient',        label: '🧘 Patient',         good: true  },
  { id: 'tired',          label: '😴 Tired',           good: false },
  { id: 'stressed',       label: '😰 Stressed',        good: false },
  { id: 'fearful',        label: '😨 Fearful',         good: false },
  { id: 'greedy',         label: '🤑 Greedy',          good: false },
  { id: 'fomo',           label: '⚡ FOMO',            good: false },
  { id: 'overconfident',  label: '😤 Overconfident',   good: false },
  { id: 'anxious',        label: '😟 Anxious',         good: false },
  { id: 'revenge',        label: '😡 Revenge Feeling', good: false },
];

const scoreColor = (s) => {
  if (!s) return THEME.colors.textMuted;
  if (s >= 8) return THEME.colors.green;
  if (s >= 6) return '#86efac';
  if (s >= 5) return THEME.colors.gold;
  if (s >= 3) return THEME.colors.orange;
  return THEME.colors.red;
};

const scoreLabel = (s) => {
  if (!s) return '-';
  if (s >= 9) return 'PEAK ⭐';
  if (s >= 7) return 'GOOD ✅';
  if (s >= 5) return 'AVERAGE ⚠️';
  if (s >= 3) return 'POOR ❌';
  return 'DANGER 🚫';
};

const today = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });

// ─── Daily Log Form ──────────────────────────────────────────────────────────
function DailyLogTab({ logs, onSave }) {
  const todayLog = logs.find(l => l.date === today()) || {};
  const [score,    setScore]    = useState(todayLog.score    || 7);
  const [emotions, setEmotions] = useState(todayLog.emotions || []);
  const [note,     setNote]     = useState(todayLog.note     || '');
  const [cause,    setCause]    = useState(todayLog.cause    || '');
  const [saved,    setSaved]    = useState(false);

  const toggleEmotion = (id) =>
    setEmotions(prev => prev.includes(id) ? prev.filter(e => e !== id) : [...prev, id]);

  const handleSave = () => {
    onSave({ score, emotions, note, cause });
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const badEmotions = emotions.filter(e => {
    const em = EMOTIONS.find(x => x.id === e);
    return em && !em.good;
  });

  return (
    <>
      <div style={S.card()}>
        <div style={S.section}>TODAY'S PSYCHOLOGY LOG</div>

        {/* Score slider */}
        <div style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
            <div style={S.label}>Mental State Score</div>
            <div>
              <span style={{ fontSize: '28px', fontWeight: '700', color: scoreColor(score) }}>{score}</span>
              <span style={{ fontSize: '12px', color: scoreColor(score), marginLeft: '6px' }}>{scoreLabel(score)}</span>
            </div>
          </div>
          <input type="range" min="1" max="10" step="1" value={score}
            onChange={e => setScore(parseInt(e.target.value))}
            style={{ width: '100%', accentColor: scoreColor(score), cursor: 'pointer' }} />
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: THEME.colors.textMuted }}>
            <span>1 — Danger</span><span>5 — Average</span><span>10 — Peak</span>
          </div>
        </div>

        {/* Trade recommendation */}
        <div style={{ padding: '8px 12px', borderRadius: '6px', marginBottom: '14px',
          background: score >= 7 ? 'rgba(0,255,136,0.08)' : score >= 5 ? 'rgba(255,215,0,0.08)' : 'rgba(255,51,51,0.08)' }}>
          <div style={{ fontSize: '12px', fontWeight: '700', color: scoreColor(score) }}>
            {score >= 7 ? '✅ SAFE TO TRADE' : score >= 5 ? '⚠️ TRADE CAREFULLY' : '🚫 DO NOT TRADE TODAY'}
          </div>
          <div style={{ fontSize: '11px', color: THEME.colors.textMuted, marginTop: '3px' }}>
            {score >= 7 ? 'Mental state is good. Stick to your rules and trade your plan.'
              : score >= 5 ? 'Below optimal. Only take A+ grade setups. Half size only.'
              : 'Mental state is too poor. Any trade today is gambling, not trading.'}
          </div>
        </div>

        {/* Emotion tags */}
        <div style={{ marginBottom: '14px' }}>
          <div style={S.label}>How are you feeling? (select all that apply)</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {EMOTIONS.map(e => {
              const active = emotions.includes(e.id);
              return (
                <button key={e.id}
                  onClick={() => toggleEmotion(e.id)}
                  style={{ background: active ? (e.good ? 'rgba(0,255,136,0.2)' : 'rgba(255,51,51,0.2)') : 'rgba(255,255,255,0.04)',
                    border: `1px solid ${active ? (e.good ? THEME.colors.green : THEME.colors.red) : THEME.colors.border}`,
                    borderRadius: '16px', padding: '4px 10px', fontSize: '11px',
                    color: active ? (e.good ? THEME.colors.green : THEME.colors.red) : THEME.colors.textMuted,
                    cursor: 'pointer', fontFamily: 'monospace' }}>
                  {e.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Warning for bad emotions */}
        {badEmotions.length >= 2 && (
          <div style={{ padding: '8px 12px', background: 'rgba(255,51,51,0.08)', borderRadius: '6px', marginBottom: '12px', fontSize: '12px', color: THEME.colors.red }}>
            ⚠️ Sir, {badEmotions.length} negative emotions detected. These are the biggest cause of trading losses. Consider skipping today.
          </div>
        )}

        {/* What's causing it */}
        <div style={{ marginBottom: '12px' }}>
          <div style={S.label}>What's affecting your state? (optional)</div>
          <input style={{ ...S.input, resize: 'none' }} placeholder="e.g. Bad sleep, yesterday's loss, news event, personal stress..."
            value={cause} onChange={e => setCause(e.target.value)} />
        </div>

        {/* Notes */}
        <div style={{ marginBottom: '14px' }}>
          <div style={S.label}>Journal note (optional)</div>
          <textarea style={{ ...S.input, minHeight: '70px' }} placeholder="How are you feeling about the market today? Any concerns?"
            value={note} onChange={e => setNote(e.target.value)} />
        </div>

        <button style={{ ...S.btn('primary'), width: '100%', padding: '10px' }} onClick={handleSave}>
          {saved ? '✅ Saved!' : 'Save Today\'s Log'}
        </button>
      </div>

      {/* JARVIS recommendation based on score */}
      <div style={{ ...S.card('rgba(255,215,0,0.06)'), padding: '12px 14px' }}>
        <div style={S.section}>JARVIS PSYCHOLOGY RULES</div>
        <div style={{ fontSize: '12px', color: THEME.colors.textMuted, lineHeight: '1.8' }}>
          <div>🎯 Score ≥ 8: Trade normally, all setups allowed</div>
          <div>⚠️ Score 5–7: Only A+ grade setups, half position size</div>
          <div>🚫 Score ≤ 4: No trading — review rules, meditate, rest</div>
          <div>😡 Revenge feeling present: Mandatory 24-hour break</div>
          <div>⚡ FOMO detected: Reduce to 25% position size only</div>
          <div>🤑 Greed active: Take profit earlier than planned</div>
        </div>
      </div>
    </>
  );
}

// ─── History / Heatmap Tab ───────────────────────────────────────────────────
function HistoryTab({ logs, trades }) {
  // Last 30 days
  const days = [];
  for (let i = 29; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    const dateStr = d.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
    const log = logs.find(l => l.date === dateStr);
    const dayTrades = trades.filter(t => t.date === dateStr && t.result !== 'OPEN');
    const wins = dayTrades.filter(t => t.result === 'WIN').length;
    days.push({ date: dateStr, log, trades: dayTrades.length, wins, day: d.toLocaleDateString('en-IN', { weekday: 'short' }) });
  }

  return (
    <>
      {/* Calendar heatmap */}
      <div style={S.card()}>
        <div style={S.section}>30-DAY PSYCHOLOGY HEATMAP</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '4px', marginBottom: '10px' }}>
          {['Mon','Tue','Wed','Thu','Fri','Sat','Sun'].map(d => (
            <div key={d} style={{ fontSize: '9px', color: THEME.colors.textMuted, textAlign: 'center' }}>{d}</div>
          ))}
          {/* Empty cells before first day */}
          {Array(new Date(days[0]?.date).getDay() === 0 ? 6 : new Date(days[0]?.date).getDay() - 1).fill(null).map((_, i) => (
            <div key={`empty-${i}`} />
          ))}
          {days.map(d => (
            <div key={d.date} title={`${d.date}: Score ${d.log?.score || '?'} | ${d.trades} trades`}
              style={{ aspectRatio: '1', borderRadius: '3px', background: d.log?.score ? scoreColor(d.log.score) + '55' : 'rgba(255,255,255,0.04)',
                border: `1px solid ${d.log?.score ? scoreColor(d.log.score) + '80' : THEME.colors.border}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '9px', color: d.log?.score ? scoreColor(d.log.score) : THEME.colors.textMuted, cursor: 'default' }}>
              {d.log?.score || ''}
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: '12px', fontSize: '10px', color: THEME.colors.textMuted }}>
          <span>🟢 ≥8 Good</span><span>🟡 5–7 Average</span><span>🔴 ≤4 Poor</span><span>⬜ Not logged</span>
        </div>
      </div>

      {/* Recent log entries */}
      <div style={S.card()}>
        <div style={S.section}>RECENT LOGS</div>
        {[...logs].reverse().slice(0, 14).map((log, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px', padding: '8px', background: 'rgba(255,255,255,0.02)', borderRadius: '6px' }}>
            <div>
              <div style={{ fontSize: '12px', color: THEME.colors.text }}>{log.date}</div>
              {log.emotions?.length > 0 && (
                <div style={{ fontSize: '10px', color: THEME.colors.textMuted, marginTop: '2px' }}>
                  {log.emotions.slice(0, 3).join(', ')}{log.emotions.length > 3 ? `+${log.emotions.length - 3}` : ''}
                </div>
              )}
              {log.note && <div style={{ fontSize: '10px', color: THEME.colors.textMuted, marginTop: '2px', maxWidth: '280px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{log.note}</div>}
            </div>
            <div style={{ textAlign: 'right', flexShrink: 0 }}>
              <div style={{ fontSize: '18px', fontWeight: '700', color: scoreColor(log.score) }}>{log.score}</div>
              <div style={{ fontSize: '9px', color: scoreColor(log.score) }}>{scoreLabel(log.score)}</div>
            </div>
          </div>
        ))}
        {logs.length === 0 && (
          <div style={{ color: THEME.colors.textMuted, fontSize: '12px', textAlign: 'center', padding: '20px' }}>
            No logs yet. Start logging daily to see patterns.
          </div>
        )}
      </div>
    </>
  );
}

// ─── Patterns Tab ────────────────────────────────────────────────────────────
function PatternsTab({ logs, trades }) {
  const closed = trades.filter(t => t.result && t.result !== 'OPEN');

  // Psych score on trading days
  const tradingDayLogs = logs.filter(l => {
    const dayTrades = closed.filter(t => t.date === l.date);
    return dayTrades.length > 0;
  });

  // Score buckets vs win rate
  const buckets = {
    high: { trades: [], label: 'High (8–10)', color: THEME.colors.green },
    mid:  { trades: [], label: 'Mid (5–7)',   color: THEME.colors.gold  },
    low:  { trades: [], label: 'Low (1–4)',   color: THEME.colors.red   },
  };
  tradingDayLogs.forEach(log => {
    const dayTrades = closed.filter(t => t.date === log.date);
    const key = log.score >= 8 ? 'high' : log.score >= 5 ? 'mid' : 'low';
    buckets[key].trades.push(...dayTrades);
  });

  // Emotion vs trade outcome
  const emotionStats = {};
  logs.forEach(log => {
    const dayTrades = closed.filter(t => t.date === log.date);
    if (!dayTrades.length) return;
    (log.emotions || []).forEach(em => {
      if (!emotionStats[em]) emotionStats[em] = { wins: 0, total: 0 };
      dayTrades.forEach(t => {
        emotionStats[em].total++;
        if (t.result === 'WIN') emotionStats[em].wins++;
      });
    });
  });

  const emotionRows = Object.entries(emotionStats)
    .filter(([, v]) => v.total >= 2)
    .map(([id, v]) => {
      const em = EMOTIONS.find(e => e.id === id);
      return { label: em?.label || id, winRate: Math.round((v.wins / v.total) * 100), total: v.total, good: em?.good };
    })
    .sort((a, b) => b.winRate - a.winRate);

  return (
    <>
      {/* Score bucket analysis */}
      <div style={S.card()}>
        <div style={S.section}>PSYCHOLOGY SCORE → WIN RATE</div>
        {Object.values(buckets).map(b => {
          const wins = b.trades.filter(t => t.result === 'WIN').length;
          const wr   = b.trades.length ? Math.round((wins / b.trades.length) * 100) : null;
          return (
            <div key={b.label} style={{ marginBottom: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                <span style={{ fontSize: '13px', color: b.color }}>{b.label}</span>
                <span style={{ fontSize: '12px', color: THEME.colors.textMuted }}>
                  {wr !== null ? `${wr}% WR (${b.trades.length} trades)` : 'No data yet'}
                </span>
              </div>
              <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: '4px', height: '8px', overflow: 'hidden' }}>
                <div style={{ width: `${wr || 0}%`, height: '100%', background: b.color, borderRadius: '4px' }} />
              </div>
            </div>
          );
        })}
        {tradingDayLogs.length < 3 && (
          <div style={{ fontSize: '11px', color: THEME.colors.textMuted, marginTop: '6px' }}>
            Log at least 3 trading days to see meaningful patterns.
          </div>
        )}
      </div>

      {/* Emotion patterns */}
      {emotionRows.length > 0 && (
        <div style={S.card()}>
          <div style={S.section}>EMOTION → WIN RATE (min 2 data points)</div>
          {emotionRows.map(r => (
            <div key={r.label} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
              <span style={{ fontSize: '11px', flex: 1 }}>{r.label}</span>
              <div style={{ width: '100px', background: 'rgba(255,255,255,0.05)', borderRadius: '3px', height: '5px', overflow: 'hidden' }}>
                <div style={{ width: `${r.winRate}%`, height: '100%', background: r.winRate >= 60 ? THEME.colors.green : r.winRate >= 45 ? THEME.colors.gold : THEME.colors.red, borderRadius: '3px' }} />
              </div>
              <span style={{ fontSize: '11px', color: THEME.colors.textMuted, width: '60px', textAlign: 'right' }}>{r.winRate}% ({r.total})</span>
            </div>
          ))}
        </div>
      )}

      {/* Average score this week */}
      {(() => {
        const recent = logs.slice(-7);
        if (!recent.length) return null;
        const avg = Math.round(recent.reduce((s, l) => s + (l.score || 0), 0) / recent.length);
        return (
          <div style={{ ...S.card(`rgba(${avg >= 7 ? '0,255,136' : avg >= 5 ? '255,215,0' : '255,51,51'},0.06)`), padding: '12px 14px', textAlign: 'center' }}>
            <div style={{ fontSize: '11px', color: THEME.colors.textMuted, marginBottom: '4px' }}>7-DAY AVERAGE SCORE</div>
            <div style={{ fontSize: '32px', fontWeight: '700', color: scoreColor(avg) }}>{avg}</div>
            <div style={{ fontSize: '12px', color: scoreColor(avg) }}>{scoreLabel(avg)}</div>
          </div>
        );
      })()}
    </>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────
export default function PsychologyTracker() {
  const [tab, setTab]   = useState('log');
  const [logs, setLogs] = useState(() => getPsychologyLogs());

  const trades = useMemo(() => getTrades(), []);

  const handleSave = (data) => {
    savePsychologyLog(data);
    setLogs(getPsychologyLogs());
  };

  const tabs = [
    { id: 'log',      label: '📝 Daily Log' },
    { id: 'history',  label: '📅 History'   },
    { id: 'patterns', label: '📊 Patterns'  },
  ];

  // Today's score strip
  const todayLog = logs.find(l => l.date === today());

  return (
    <div style={S.page}>
      <div style={S.title}>🧘 PSYCHOLOGY TRACKER</div>
      <div style={S.sub}>MENTAL STATE · EMOTION LOG · TRADING DISCIPLINE</div>

      {/* Today strip */}
      <div style={{ ...S.card(todayLog ? `rgba(${todayLog.score >= 7 ? '0,255,136' : todayLog.score >= 5 ? '255,215,0' : '255,51,51'},0.08)` : 'rgba(255,255,255,0.03)'), padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: '11px', color: THEME.colors.textMuted }}>TODAY'S STATE</div>
          <div style={{ fontSize: '13px', fontWeight: '600', color: todayLog ? scoreColor(todayLog.score) : THEME.colors.textMuted }}>
            {todayLog ? scoreLabel(todayLog.score) : 'Not logged yet'}
          </div>
          {todayLog?.emotions?.length > 0 && (
            <div style={{ fontSize: '10px', color: THEME.colors.textMuted }}>{todayLog.emotions.slice(0, 3).join(' · ')}</div>
          )}
        </div>
        <div style={{ fontSize: '32px', fontWeight: '700', color: todayLog ? scoreColor(todayLog.score) : THEME.colors.textMuted }}>
          {todayLog?.score || '?'}
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '6px', marginBottom: '14px' }}>
        {tabs.map(t => (
          <button key={t.id} style={{ ...S.tabBtn(tab === t.id), padding: '7px 4px' }}
            onClick={() => setTab(t.id)}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'log'      && <DailyLogTab logs={logs} onSave={handleSave} />}
      {tab === 'history'  && <HistoryTab  logs={logs} trades={trades} />}
      {tab === 'patterns' && <PatternsTab logs={logs} trades={trades} />}
    </div>
  );
}


